import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const OBJECTIVES = [
  { id: "ventas", label: "Ventas", desc: "Maximizar conversiones y compras" },
  { id: "awareness", label: "Awareness", desc: "Hacer conocida la marca" },
  { id: "engagement", label: "Engagement", desc: "Crear comunidad e interacción" },
  { id: "leads", label: "Leads", desc: "Captar prospectos calificados" },
  { id: "lanzamiento", label: "Lanzamiento", desc: "Anunciar producto/servicio nuevo" },
  { id: "comunidad", label: "Comunidad", desc: "Construir audiencia leal" },
];
const TONES = [
  { id: "profesional", label: "Profesional" },
  { id: "casual", label: "Casual" },
  { id: "divertido", label: "Divertido" },
  { id: "agresivo", label: "Agresivo" },
  { id: "inspiracional", label: "Inspiracional" },
  { id: "educativo", label: "Educativo" },
];
const NETWORKS = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "twitter", label: "X / Twitter" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "tiktok", label: "TikTok" },
];

export default function CampaignWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasVideo = !!user?.has_video_addon;

  const today = new Date();
  const inMonth = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    brand_id: "",
    start_date: today.toISOString().slice(0, 10),
    end_date: inMonth.toISOString().slice(0, 10),
    objective: "engagement",
    tone: "casual",
    products: "",
    audience_age: "",
    audience_gender: "",
    audience_interests: "",
    geo_country: "",
    geo_city: "",
    hashtags: "",
    networks: ["instagram", "facebook"],
    cadence: { posts_per_day: 1, images_per_day: 0, videos_per_day: 0 },
  });

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      setBrands(data || []);
      if (data?.[0]?.id && !form.brand_id) setForm((f) => ({ ...f, brand_id: data[0].id }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updCad = (patch) => setForm((f) => ({ ...f, cadence: { ...f.cadence, ...patch } }));

  const toggleNet = (id) => {
    setForm((f) => ({
      ...f,
      networks: f.networks.includes(id) ? f.networks.filter((n) => n !== id) : [...f.networks, id],
    }));
  };

  const totalItems = (() => {
    const days = Math.max(1, Math.round((new Date(form.end_date) - new Date(form.start_date)) / (1000 * 60 * 60 * 24)) + 1);
    const perDay = (form.cadence.posts_per_day + form.cadence.images_per_day + form.cadence.videos_per_day) * (form.networks.length || 1);
    return days * perDay;
  })();

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        start_date: new Date(form.start_date + "T08:00:00Z").toISOString(),
        end_date: new Date(form.end_date + "T23:59:59Z").toISOString(),
      };
      const { data } = await api.post("/campaigns", payload);
      toast.success(`Campaña creada con ${data.items_planned} piezas planificadas`);
      navigate(`/dashboard/campaigns/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear campaña");
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = (() => {
    if (step === 1) return form.name && form.brand_id && form.start_date && form.end_date;
    if (step === 2) return form.objective && form.tone;
    if (step === 3) return form.networks.length > 0;
    if (step === 4) return form.cadence.posts_per_day + form.cadence.images_per_day + form.cadence.videos_per_day > 0;
    return true;
  })();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="campaign-wizard">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">OPERATIVO · NUEVA CAMPAÑA</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-none">Crear campaña</h1>
        <p className="font-mono-data text-sm text-[#5C5C5C] mt-3">Paso {step} de 5</p>
        <div className="flex gap-1 mt-2">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className={`h-1 flex-1 ${s <= step ? "bg-[#E53935]" : "bg-[#F4F4F0]"}`} />
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#0A0A0A] p-6 space-y-4">
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl">1. Identidad de la campaña</h2>
            <Field label="NOMBRE DE LA CAMPAÑA">
              <input type="text" value={form.name} onChange={(e) => upd({ name: e.target.value })} placeholder="Ej: Black Friday 2026" data-testid="wiz-name" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" />
            </Field>
            <Field label="MARCA">
              <select value={form.brand_id} onChange={(e) => upd({ brand_id: e.target.value })} data-testid="wiz-brand" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm">
                <option value="">Selecciona…</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.brand_name}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="INICIO">
                <input type="date" value={form.start_date} onChange={(e) => upd({ start_date: e.target.value })} data-testid="wiz-start" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" />
              </Field>
              <Field label="FIN">
                <input type="date" value={form.end_date} onChange={(e) => upd({ end_date: e.target.value })} data-testid="wiz-end" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" />
              </Field>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl">2. Objetivo y tono</h2>
            <Field label="OBJETIVO PRINCIPAL">
              <div className="grid grid-cols-2 gap-2">
                {OBJECTIVES.map((o) => (
                  <button key={o.id} onClick={() => upd({ objective: o.id })} data-testid={`wiz-obj-${o.id}`} className={`text-left p-3 border ${form.objective === o.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A] hover:bg-[#F4F4F0]"}`}>
                    <div className="font-mono-mark text-[10px]">{o.label.toUpperCase()}</div>
                    <div className="font-mono-data text-xs mt-1 opacity-70">{o.desc}</div>
                  </button>
                ))}
              </div>
            </Field>
            <Field label="TONO DE COMUNICACIÓN">
              <div className="flex flex-wrap gap-2">
                {TONES.map((t) => (
                  <button key={t.id} onClick={() => upd({ tone: t.id })} data-testid={`wiz-tone-${t.id}`} className={`font-mono-mark text-[10px] px-3 py-2 border ${form.tone === t.id ? "bg-[#E53935] text-white border-[#E53935]" : "border-[#0A0A0A]"}`}>
                    {t.label.toUpperCase()}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="PRODUCTOS / SERVICIOS A DESTACAR (opcional)">
              <textarea value={form.products} onChange={(e) => upd({ products: e.target.value })} rows={3} data-testid="wiz-products" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" placeholder="Ej: Plan Premium 50% off, asesoría 1-on-1 gratis con la suscripción anual" />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl">3. Audiencia, geo y redes</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="EDAD"><input type="text" value={form.audience_age} onChange={(e) => upd({ audience_age: e.target.value })} placeholder="25-45" data-testid="wiz-age" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" /></Field>
              <Field label="GÉNERO"><input type="text" value={form.audience_gender} onChange={(e) => upd({ audience_gender: e.target.value })} placeholder="Mixto / F / M" data-testid="wiz-gender" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" /></Field>
              <Field label="INTERESES"><input type="text" value={form.audience_interests} onChange={(e) => upd({ audience_interests: e.target.value })} placeholder="Marketing, IA, automatización" data-testid="wiz-interests" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="PAÍS (opcional)"><input type="text" value={form.geo_country} onChange={(e) => upd({ geo_country: e.target.value })} placeholder="MX, AR, CO, ES…" data-testid="wiz-country" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" /></Field>
              <Field label="CIUDAD (opcional)"><input type="text" value={form.geo_city} onChange={(e) => upd({ geo_city: e.target.value })} placeholder="CDMX, Bogotá…" data-testid="wiz-city" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" /></Field>
            </div>
            <Field label="HASHTAGS BASE (opcional)">
              <input type="text" value={form.hashtags} onChange={(e) => upd({ hashtags: e.target.value })} placeholder="#cia #marketing #ia" data-testid="wiz-hashtags" className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" />
            </Field>
            <Field label="REDES SOCIALES">
              <div className="flex flex-wrap gap-2">
                {NETWORKS.map((n) => (
                  <button key={n.id} onClick={() => toggleNet(n.id)} data-testid={`wiz-net-${n.id}`} className={`font-mono-mark text-[10px] px-3 py-2 border ${form.networks.includes(n.id) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]"}`}>
                    {n.label.toUpperCase()}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl">4. Cadencia de publicación</h2>
            <p className="font-mono-data text-xs text-[#5C5C5C]">Define cuántas piezas se generarán por red, por día.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <CadenceField label="POSTS DE TEXTO / DÍA" value={form.cadence.posts_per_day} onChange={(v) => updCad({ posts_per_day: v })} testid="wiz-posts" />
              <CadenceField label="IMÁGENES IA / DÍA" value={form.cadence.images_per_day} onChange={(v) => updCad({ images_per_day: v })} testid="wiz-images" />
              <CadenceField label="VIDEOS IA / DÍA" value={form.cadence.videos_per_day} onChange={(v) => updCad({ videos_per_day: v })} testid="wiz-videos" disabled={!hasVideo} max={3} />
            </div>
            {!hasVideo && form.cadence.videos_per_day === 0 && (
              <div className="font-mono-data text-xs text-[#5C5C5C] bg-[#F4F4F0] border-l-4 border-[#FFD600] p-3">
                ⚡ Para incluir videos en tus campañas, activa el add-on de Video desde <a href="/dashboard/subscription" className="underline">Suscripción</a>.
              </div>
            )}
            <div className="bg-[#0A0A0A] text-white p-4 mt-4">
              <div className="font-mono-mark text-[10px] text-[#FFD600]">RESUMEN ESTIMADO</div>
              <div className="font-display text-3xl mt-1">{totalItems} piezas</div>
              <div className="font-mono-data text-xs opacity-70 mt-1">en {form.networks.length} red(es) · sobre {Math.max(1, Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1)} días</div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl">5. Revisar y activar</h2>
            <Summary form={form} totalItems={totalItems} brandName={brands.find((b) => b.id === form.brand_id)?.brand_name} />
            <button onClick={submit} disabled={submitting} data-testid="wiz-submit" className="w-full bg-[#E53935] text-white font-mono-mark text-[10px] px-5 py-4 hover:bg-[#0A0A0A] disabled:opacity-50 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4" /> {submitting ? "CREANDO…" : "ACTIVAR CAMPAÑA AUTÓNOMA"}
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="wiz-prev" className="font-mono-mark text-[10px] px-4 py-3 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-30 flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> ANTERIOR
        </button>
        {step < 5 && (
          <button onClick={() => setStep(step + 1)} disabled={!canNext} data-testid="wiz-next" className="font-mono-mark text-[10px] px-4 py-3 bg-[#0A0A0A] text-white hover:bg-[#E53935] disabled:opacity-30 flex items-center gap-1">
            SIGUIENTE <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="font-mono-mark text-[9px] text-[#5C5C5C] mb-1">{label}</div>
      {children}
    </div>
  );
}

function CadenceField({ label, value, onChange, testid, disabled, max = 5 }) {
  return (
    <div className={disabled ? "opacity-40" : ""}>
      <div className="font-mono-mark text-[9px] text-[#5C5C5C] mb-1">{label}</div>
      <input type="number" min={0} max={max} value={value} disabled={disabled} onChange={(e) => onChange(Math.max(0, Math.min(max, Number(e.target.value) || 0)))} data-testid={testid} className="w-full border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm" />
    </div>
  );
}

function Summary({ form, totalItems, brandName }) {
  const Row = ({ k, v }) => v ? (<div className="flex gap-3 py-1 border-b border-[#F4F4F0]"><div className="font-mono-mark text-[9px] text-[#5C5C5C] w-32 flex-shrink-0">{k}</div><div className="font-mono-data text-xs flex-1">{v}</div></div>) : null;
  return (
    <div className="bg-[#F4F4F0] p-4">
      <Row k="NOMBRE" v={form.name} />
      <Row k="MARCA" v={brandName || form.brand_id} />
      <Row k="FECHAS" v={`${form.start_date} → ${form.end_date}`} />
      <Row k="OBJETIVO" v={form.objective} />
      <Row k="TONO" v={form.tone} />
      <Row k="PRODUCTOS" v={form.products} />
      <Row k="AUDIENCIA" v={[form.audience_age, form.audience_gender, form.audience_interests].filter(Boolean).join(" · ")} />
      <Row k="GEO" v={[form.geo_country, form.geo_city].filter(Boolean).join(", ")} />
      <Row k="HASHTAGS" v={form.hashtags} />
      <Row k="REDES" v={form.networks.join(", ")} />
      <Row k="CADENCIA" v={`${form.cadence.posts_per_day} posts · ${form.cadence.images_per_day} imágenes · ${form.cadence.videos_per_day} videos / día / red`} />
      <Row k="TOTAL PIEZAS" v={`${totalItems}`} />
    </div>
  );
}
